import { useContext, useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { useNavigate } from "react-router-dom";
import { Header, FormSkeleton } from "../../components";
import {
  Button,
  PickList,
  TransactionalSubheader,
  SelectButton,
  Card,
  Dropdown,
  InputText,
} from "iuvity-ui-components";
import { CustomFormBox } from "../../components/points/CustomFormBox";
import { CustomDialog, DialogProps } from "../../components/dialog/CustomDialog";
import { AppContext } from "../../context/index";
import { useServices } from "../../hooks/useServices";
import { useMasters } from "../../hooks/useMasters";
import { mastersFormatter } from "../../utils";
import { basicInfoBoxFields, locationBoxFields, calendarTranslations } from "../../constants";
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useSchedules } from "../../hooks/useSchedules";
import { PointsOfAttentionFormSchema } from "../../validations/schemas";
import { usePointOfAttention } from "../../hooks/usePointsOfAttention";
import { usePointsParams } from "../../hooks/usePointsParams";
import { formatHour12, formatStringToDate } from "../../helpers/formatDate";
import { FileUpload } from 'primereact/fileupload';
import { Badge } from "iuvity-ui-components/dist/badge";
import { REVALIDATE_FORM_PROPS } from "../../constants";

interface NewPointOfAttentionPageProps {
    update?: boolean;
    updateMultiple?: boolean;
}

// Inicializando los campos del formulario
let formBlankValues:any = {
    pointServiceType: '',
    phone: '',
    name: '',
    code: '',
    observation: '',
    country: '',
    department: '',
    city:'',
    address: '',
    latitude: '',
    longitude: '',
    serviceIds: [],
    scheduleIds: [],
    file: '',
    startDate: '',
    endDate: '',
}

export const NewPointOfAttentionPage: React.FC<NewPointOfAttentionPageProps> = ({
    update = false,
    updateMultiple = false,
  }) => {
    const navigate = useNavigate();
    const {
        t,
        appState: { user, selectedPointsObject },
        setAppState,
    } = useContext(AppContext);

    // Tomando valores del context en caso de Editar
    const formValues = update ? {
        pointServiceType: selectedPointsObject?.pointServiceTypeId,
        phone: selectedPointsObject?.phone,
        name: selectedPointsObject?.name,
        code: selectedPointsObject?.code,
        observation: selectedPointsObject?.observation,
        country: selectedPointsObject?.countryId,
        department: selectedPointsObject?.stateId,
        city: selectedPointsObject?.cityId,
        address: selectedPointsObject?.address,
        latitude: selectedPointsObject?.latitude,
        longitude: selectedPointsObject?.longitude,
        serviceIds: selectedPointsObject?.serviceIds,
        scheduleIds: selectedPointsObject?.scheduleIds,
        file: selectedPointsObject?.file || '',
        startDate: formatStringToDate(selectedPointsObject?.startDate),
        endDate: formatStringToDate(selectedPointsObject?.endDate),
    } : {...formBlankValues}

    const form = useForm({
        mode: "onChange",
        delayError: 1200,
        reValidateMode: "onChange",
        defaultValues: formValues,
    });
    const {
        control,
        getValues,
        setValue,
        watch,
        resetField,
        formState: { errors, isDirty, isValid },
    } = form

    // Instanciando watchers de cada campo para validar disabled del botón submit
    const nameValues = useWatch({
      control: control,
      name: "name",
    });
  
    const observationValues = useWatch({
      control: control,
      name: "observation",
    });
  
    const phoneValues = useWatch({
      control: control,
      name: "phone",
    });

    const codeValues = useWatch({
      control: control,
      name: "code",
    });
  
    const pointServiceTypeValues = useWatch({
      control: control,
      name: "pointServiceType",
    });

    const countryValues = useWatch({
      control: control,
      name: "country",
    });

    const departmentValues = useWatch({
      control: control,
      name: "department",
    });

    const cityValues = useWatch({
      control: control,
      name: "city",
    });

    const addressValues = useWatch({
      control: control,
      name: "address",
    });

    const latitudeValues = useWatch({
      control: control,
      name: "latitude",
    });

    const longitudeValues = useWatch({
      control: control,
      name: "longitude",
    });

    const serviceIdsValues = useWatch({
      control: control,
      name: "serviceIds",
    });

    const scheduleIdsValues = useWatch({
      control: control,
      name: "scheduleIds",
    });

    const fileValues = useWatch({
      control: control,
      name: "file",
    });

    const { listCountries, listDepartments, listCities } = usePointsParams();
    const [departments, setDepartments] = useState([])
    const [cities, setCities] = useState([])
    const { dataService } = useServices("DATA");
    const { pointOfAttentionAction } = usePointOfAttention("DATA");
    const [serviceSource, setServiceSource] = useState<any>([])
    const [serviceTarget, setServiceTarget] = useState<any>([])
    const { dataSchedule } = useSchedules("DATA", "no-translate");
    const [daysOptions, setDaysOptions] = useState<any>([])
    const [hoursOptions, setHoursOptions] = useState<any>([])
    const [currentScheduleId, setCurrentScheduleId] = useState<number>(0)
    const [schedulesSource, setSchedulesSource] = useState<any>(dataSchedule)
    const [schedulesSelected, setSchedulesSelected] = useState<any>([])
    const [schedulesToShow, setSchedulesToShow] = useState<any>([])
    const { dataMaster } = useMasters("DATA")
    const [disableUploadFile, setDisableUploadFile] = useState<boolean>(false)
    const [disabledFieldsObject, setDisabledFieldsObject] = useState<any>({
      department: true,
      city: true,
      day: true,
      hour: true
    })
    const optionalFieldsObject = {
      startDate: true,
      endDate: true,
    }
    const [isOkToLoaded, setIsOkToLoaded] = useState<boolean>(true)
    const [calendarOptions, setCalendarOptions] = useState<any>({
      startDate: {
        value: undefined,
        minDate: new Date(),
        maxDate: undefined,
        disabled: false,
      },
      endDate: {
        value: undefined,
        minDate: new Date(),
        maxDate: undefined,
        disabled: (update && formValues?.endDate !== '') ? false : true,
      },
    })

    // Efecto para setear los dropdowns dependientes pais/estado/ciudad
    const [countCountry, setCountCountry] = useState<number>(0)
    useEffect(()=> {
      if(countryValues !== "") {
        if (update) {
          if (countryValues !== selectedPointsObject?.countryId || countCountry>0) {
            setValue('department', '', REVALIDATE_FORM_PROPS);
            setValue('city', '', REVALIDATE_FORM_PROPS);
            setCountCountry(countCountry + 1);
          }
        }
        else {
          resetField('department');
          resetField('city');
        }
        // @ts-ignore
        setDisabledFieldsObject((prev) => ({
          ...prev,
          department: false,
          city: true,
        }))
        setDepartments(listDepartments.reduce((acc:any, item:any)=>{
          const arrAux = acc.map((obj:any) => obj.value)
          if(item?.countryId === countryValues && !arrAux.includes(item.value)){
            acc.push({
              label: item.label,
              value: item.value,
            });
          }
          return acc;
        },[])
        ?.sort((a: any, b: any) => a.label.localeCompare(b.label)))
      }
    }, [countryValues, listDepartments])

    const [countDept, setCountDept] = useState<number>(0)
    useEffect(()=> {
      if(departmentValues !== "") {
        if (update) {
          (departmentValues !== selectedPointsObject?.stateId || countDept>2) && setValue('city', '', REVALIDATE_FORM_PROPS);
          setCountDept(countDept + 1);
        }
        else resetField('city');
        // @ts-ignore
        setDisabledFieldsObject((prev) => ({
          ...prev,
          city: false,
        }))
        setCities(listCities.reduce((acc:any, item:any)=>{
          const arrAux = acc.map((obj:any) => obj.value)
          if(item?.stateId === departmentValues && !arrAux.includes(item.value)){
            acc.push({
              label: item.label,
              value: item.value,
            });
          }
          return acc;
        },[])
        ?.sort((a: any, b: any) => a.label.localeCompare(b.label)))
      }
    }, [departmentValues, listDepartments, listCities])

    const formKeysOptions:any = {
        pointServiceType: mastersFormatter(dataMaster),
        country: listCountries,
        department: departments,
        city: cities,
        typeOfSchedule: schedulesSource
          ?.reduce((acc:any, item:any)=>{
            const arrAux = acc.map((obj:any) => obj.value)
            if(!arrAux.includes(item.scheduleTypeId)){
              acc.push({
                label: t(`PointsOfAttention.typeOfSchedule${item.scheduleType.name === 'NORMAL' ? 'REGULAR' : item.scheduleType.name}`),
                value: item.scheduleTypeId,
              });
            }
            return acc;
          },[])
          ?.sort((a: any, b: any) => a.label.localeCompare(b.label)),
        day: daysOptions,
        hour: hoursOptions,
    };

    useEffect(()=>{
      if (update) {
        let schedulesSelectedAux:any = []
        let schedulesSourceAux:any = []
        dataSchedule?.map((item:any) => {
          selectedPointsObject?.scheduleIds.includes(item.id)
          ? schedulesSelectedAux.push(item)
          : schedulesSourceAux.push(item)
        })
        setSchedulesSource(schedulesSourceAux)
        setSchedulesSelected(schedulesSelectedAux)
      }
      else {
        setSchedulesSource(dataSchedule)
      }
    },[dataSchedule])

    useEffect(()=>{
      if (dataService && dataService.length > 0) {
        if (update) {
          let arrSource:any = []
          let arrTarget:any = []
          dataService.map((field:any)=>{
            const item = {
              id: field.id,
              label: field.name,
            }
            selectedPointsObject?.serviceIds.includes(field.id)
            ? arrTarget.push(item)
            : arrSource.push(item)
          })
          setServiceSource(arrSource);
          setServiceTarget(arrTarget);
        }
        else setServiceSource(
            dataService.map((field: any) => ({
              id: field.id,
              label: field.name,
            }))
          ?.sort((a: any, b: any) => a.label.localeCompare(b.label))
        )
      }
    },[dataService])

    const [modalProps, setModalProps] = useState<DialogProps>({
        visible: false,
    } as DialogProps);

    const onReturn = () => {
        setAppState((prev) => ({
        ...prev,
        selectedPointsObject: null,
        activeIndexPoints: 0,
        }));
        navigate('/pointsofattention');
        window.scrollTo(0, 0);
    };

    const onAction = async () => {
        const formValues = getValues();
        const actionResult = await pointOfAttentionAction(update ? "UPDATE" : updateMultiple ? "UPDATEMULTIPLE" : "CREATE",
        update ? {...formValues, id: selectedPointsObject.id} : updateMultiple ? {...formValues, ids: selectedPointsObject} : formValues);
        if (actionResult === "SUCCESS") {
        setAppState((prev) => ({
            ...prev,
            activeIndexPoints: 0,
        }));
        navigate("/pointsofattention");
        window.scrollTo(0, 0);
        }
    };

    const handleModalProps = (actionType: "action" | "cancel", goHome?: boolean) => {
        switch (actionType) {
        case "action":
            return setModalProps((prev) => ({
            ...prev,
            visible: true,
            title: t(`PointsOfAttention.${update ? "save" : "create"}ModalTitle`),
            content: t(`PointsOfAttention.${update ? "save" : updateMultiple ? "save" : "create"}MasterModalContent`).replace('tipo ', '').replace('type ', ''),
            onConfirm: onAction,
            }));
        case "cancel":
            return setModalProps((prev) => ({
            ...prev,
            visible: true,
            title: t("Users.cancelModalTitle"),
            content: t("Users.cancelModalContent"),
            onConfirm: goHome ? () => navigate('/') : onReturn,
            }));
        default:
            return;
        }
    };

    // DAYS-SELECTBUTTON
    const [valueDays, setValueDays] = useState(null);
    const items = [
        { name: t('PointsOfAttention.dayMONDAY').slice(0,2), value: 'MONDAY' },
        { name: t('PointsOfAttention.dayTUESDAY').slice(0,2), value: 'TUESDAY' },
        { name: t('PointsOfAttention.dayWEDNESDAY').slice(0,2), value: 'WEDNESDAY' },
        { name: t('PointsOfAttention.dayTHURSDAY').slice(0,2), value: 'THURSDAY' },
        { name: t('PointsOfAttention.dayFRIDAY').slice(0,2), value: 'FRIDAY' },
        { name: t('PointsOfAttention.daySATURDAY').slice(0,2), value: 'SATURDAY' },
        { name: t('PointsOfAttention.daySUNDAY').slice(0,2), value: 'SUNDAY' }
    ];

    // ESTADOS DE DROPDOWNS
    const dropdownInitStates = {
      typeOfSchedule: '',
      day: '',
      hour: '',
    }
    const [dropdownStates, setDropdownStates] = useState(dropdownInitStates)

    // Efecto para actualizar dias con multilanguage
    useEffect(() => {
      const handleResetOptionTypeOfSchedule = (() => {
        setDropdownStates(dropdownInitStates);
        setDisabledFieldsObject((prev:any) => ({
          ...prev,
          day: true,
          hour: true,
        }))
      }) as EventListener;
  
      window.addEventListener(
        "CHANGE::LANGUAGE",
        handleResetOptionTypeOfSchedule
      );
      return () =>
        window.removeEventListener(
          "CHANGE::LANGUAGE",
          handleResetOptionTypeOfSchedule
        );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ACCIONES DEL PICKLIST
    const onMoveToSource = (e: any) => {
      if (!serviceSource.find((elem: any) => elem.label === e.value[0].label)) {
        const target = [...serviceTarget.filter((field: any) => field.id !== e.value[0].id)]
        setServiceTarget(target);
        setValue('serviceIds', target.map((service:any)=>service.id))
        // @ts-ignore
        setServiceSource(
          [...serviceSource, ...e.value]?.sort((a, b) => a.label.localeCompare(b.label))
        );
      }
    };
  
    const onMoveToTarget = (e: any) => {
      setServiceSource(
        [...serviceSource.filter((field: any) => field.id !== e.value[0].id)]?.sort(
          (a, b) => a.label.localeCompare(b.label)
        )
      );
      const target = [...serviceTarget, ...e.value]
      setServiceTarget(target);
      setValue('serviceIds', target.map((service:any)=>service.id))
    };
  
    const onMoveAllToSource = (e: any) => {
      setServiceTarget([]);
      setValue('serviceIds', [])
      setServiceSource(
        [...serviceSource, ...e.value]?.sort((a, b) => a.label.localeCompare(b.label))
      );
    };
  
    const onMoveAllToTarget = (e: any) => {
      setServiceSource([]);
      const target = [...serviceTarget, ...e.value]
      setServiceTarget(target);
      setValue('serviceIds', target.map((service:any)=>service.id))
    };

    // EFECTO PARA CONTROLAR CARACTERES NO PERMITIDOS SIN REGEX
    useEffect(()=>{
      watch((value)=>{
        if (
          value.code.includes('´')
          || value.code.includes('^')
          || value.code.includes('`')
          || value.code.includes('¨')
          || value.code.includes('~')
        ) {
          setValue('code', value.code.replace('´', '').replace('^', '').replace('`', '').replace('¨', '').replace('~', ''), REVALIDATE_FORM_PROPS)
        }
        if (
          value.latitude.toString().includes('´')
          || value.latitude.toString().includes('^')
          || value.latitude.toString().includes('`')
          || value.latitude.toString().includes('¨')
          || value.latitude.toString().includes('~')
        ) {
          setValue('latitude', value.latitude.replace('´', '').replace('^', '').replace('`', '').replace('¨', '').replace('~', ''), REVALIDATE_FORM_PROPS)
        }
        if (
          value.longitude.toString().includes('´')
          || value.longitude.toString().includes('^')
          || value.longitude.toString().includes('`')
          || value.longitude.toString().includes('¨')
          || value.longitude.toString().includes('~')
        ) {
          setValue('longitude', value.longitude.replace('´', '').replace('^', '').replace('`', '').replace('¨', '').replace('~', ''), REVALIDATE_FORM_PROPS)
        }
      })
    },[watch])

    const readOnlyObject = {
      pointServiceType: update ? true : false,
      code: update ? true : false,
    }

    return (
        <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <div className="flex flex-row align-items-center">
              <i
                className="iu iu-icon-left pr-3"
                onClick={() => isDirty ? handleModalProps("cancel") : onReturn()}
                aria-hidden="true"
                style={{
                  fontSize: "var(--font-size-tablet-title)",
                  cursor: "pointer",
                }}
              />
              <div className="flex alig-items-center">
                {
                  update ? t("PointsOfAttention.editPointOfAttentionTitle") : 
                  updateMultiple ? t("PointsOfAttention.editMultiplePointOfAttentionTitle") : t("PointsOfAttention.createPointOfAttentionTitle")
                }
                {
                  update
                    ?
                      <Badge
                        className={'flow-badge ml-3'}
                        value={selectedPointsObject?.name}
                      />  
                    : null
                }
              </div>
            </div>
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t(`PointsOfAttention.${update ? 'edit' : 'create'}PointOfAttentionTitle`)}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => isDirty ? handleModalProps("cancel", true) : navigate('/'),
          }}
          breadcrumbsItems={[
            {
              label: t("PointsOfAttention.pointsTitle"),
              command: () => isDirty ? handleModalProps("cancel") : onReturn(),
            },
            {
              label: t(`PointsOfAttention.${update ? 'edit' : updateMultiple ? 'editMultiple' : 'create'}PointOfAttentionBreadcrumb`),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      {/* Caja de Información Básica */}
      <CustomFormBox
          boxTitle={t('PointsOfAttention.basicInfoTitle')}
          fields= {updateMultiple ? basicInfoBoxFields.filter((item) => item.key === 'observation') : basicInfoBoxFields}
          form= {form}
          dropdownOptions={formKeysOptions}
          updateMultiple={updateMultiple}
          isOptional={optionalFieldsObject}
          schemaOfValidation={PointsOfAttentionFormSchema}
          language={localStorage.getItem('LANGUAGE') || 'en'}
          calendarTranslations={calendarTranslations}
          calendarOptions={calendarOptions}
          setCalendarOptions={setCalendarOptions}
          REVALIDATE_FORM_PROPS={REVALIDATE_FORM_PROPS}
          readOnlyObject={readOnlyObject}
      />

      {/* Caja de Ubicación */}
      { !updateMultiple &&
        <div id="location-points">
          <CustomFormBox
            boxTitle={t('PointsOfAttention.locationTitle')}
            fields= {locationBoxFields}
            form= {form}
            dropdownOptions={formKeysOptions}
            disabledFieldsObject={disabledFieldsObject}
            schemaOfValidation={PointsOfAttentionFormSchema}
          />
        </div>
      }

      {/* Caja Picklist de Servicios */}
      <div className="picklist-points mt-4">
        <div className="picklist-points--container">
          <div className="picklist-points--container__title">
              <p className="boxTitle mb-4">
                  {t('PointsOfAttention.servicesTabTitle')}
              </p>
          </div>
          <div className="picklist-points--container__subtitle">
              <p className="boxSubtitle" style={{paddingLeft:"4rem"}}>
                  {t('PointsOfAttention.servicesComponentDescription')}
              </p>
          </div>
          <PickList
            dataKey="id"
            id="pick-list"
            className="picklist-points--container__component mt-4"
            showSourceControls={false}
            showTargetControls={false}
            sourceHeader={t("PointsOfAttention.servicesPickLabel")}
            targetHeader={t("PointsOfAttention.selectedServicesPickLabel")}
            source={serviceSource}
            target={serviceTarget}
            onMoveAllToSource={onMoveAllToSource}
            onMoveAllToTarget={onMoveAllToTarget}
            onMoveToSource={onMoveToSource}
            onMoveToTarget={onMoveToTarget}
          />
          <Controller
            key='serviceIds'
            name='serviceIds'
            control={control}
            rules={(PointsOfAttentionFormSchema(t) as any).serviceIds}
            render={({ field }) => {
                return (
                  <InputText
                    id={`input-serviceIds`}
                    style={{
                    visibility: "hidden",
                    height: '0',
                    }}
                    {...field}
                  />
                );
              }}
          />
          <ErrorMessage
            key='error-serviceIds'
            errors={errors}
            name='serviceIds'
            //@ts-ignore
            render={({ message }) => (
            <small
                className="p-error block"
                style={{
                marginTop: "0.5rem",
                marginBottom: "1.5rem",
                }}
            >
                {message}
            </small>
            )}
          />
        </div>
      </div>

      {/* Caja de Horarios */}
      <div className="schedules-points mt-2">
        <div className="schedules-points--container">
          <div className="schedules-points--container__title">
              <p className="boxTitle">
                  {t('PointsOfAttention.schedulesTabTitle')}
              </p>
          </div>
          <div>
          <div className={`container-treeboxes mt-4`} id='PreparationFormContainer'>
            <div className={`treeboxes-child general-form start schedules-points--container-box-one`}>
              <div className="treeboxes-item treeboxes-item__gray schedules-treeboxes" style={{ height: '80%' }} id='SelectScheduleContainer'>
                  <p className="points-subtitle">{t("PointsOfAttention.schedulesOneBoxDescription")}</p>
                  <div className='dropdowns-container'>
                    {
                      schedulesSource
                        ? (['typeOfSchedule', 'day', 'hour'].map((key) => (
                          <Dropdown
                            key={key}
                            id={`dropdown-${key}`}
                            className={`dropdown mb-3`}
                            label={t(`PointsOfAttention.${key}Label`)}
                            placeholder={!update && t(`PointsOfAttention.${key}Placeholder`)}
                            options={formKeysOptions[key]}
                            showClear={false}
                            //@ts-ignore
                            value={dropdownStates[key]}
                            onChange={(e)=>{
                              setDropdownStates((prev) => ({
                                ...prev,
                                [key]: e.value
                              }));
                              if (key === 'typeOfSchedule') {
                                // @ts-ignore
                                setDisabledFieldsObject((prev) => ({
                                  ...prev,
                                  day: false,
                                  hour: true,
                                }))
                                setDropdownStates((prev) => ({
                                  ...prev,
                                  day: ''
                                }));
                                !dropdownStates[key] && setDropdownStates((prev) => ({
                                  ...prev,
                                  hour: ''
                                }));
                                const daysFilterArray = schedulesSource?.reduce((acc:any, item:any)=>{
                                  const arrAux = acc.map((obj:any) => obj.value)
                                  if(item.scheduleTypeId === e.value && !arrAux.includes(item.day)){
                                    acc.push({
                                      label: t(`PointsOfAttention.day${item.day}`),
                                      value: item.day,
                                    });
                                  }
                                  return acc;
                                },[])
                                setDaysOptions([
                                  t("PointsOfAttention.dayMONDAY"),
                                  t("PointsOfAttention.dayTUESDAY"),
                                  t("PointsOfAttention.dayWEDNESDAY"),
                                  t("PointsOfAttention.dayTHURSDAY"),
                                  t("PointsOfAttention.dayFRIDAY"),
                                  t("PointsOfAttention.daySATURDAY"),
                                  t("PointsOfAttention.daySUNDAY")
                                ]
                                .map((orderDay)=> 
                                  daysFilterArray.find((item:any)=>item.label === orderDay))
                                .filter((item)=>item!=undefined)
                                )
                              }
                              if (key === 'day') {
                                // @ts-ignore
                                setDisabledFieldsObject((prev) => ({
                                  ...prev,
                                  hour: false,
                                }))
                                setDropdownStates((prev) => ({
                                  ...prev,
                                  hour: ''
                                }));
                                setHoursOptions(schedulesSource
                                  ?.sort(function (a:any, b:any) {
                                    if (a.scheduleTimes[0].start > b.scheduleTimes[0].start) {
                                      return 1;
                                    }
                                    if (a.scheduleTimes[0].start < b.scheduleTimes[0].start) {
                                      return -1;
                                    }
                                    return 0;
                                  })
                                  ?.map((item:any)=>{
                                    if (
                                      dropdownStates.typeOfSchedule === item.scheduleTypeId
                                      && e.value === item.day
                                    )
                                    return {
                                      label:
                                        `${formatHour12(item.scheduleTimes[0].start)} - 
                                        ${formatHour12(item.scheduleTimes[0].end)}`,
                                      value: item.id,
                                    }
                                  })
                                  .filter((item:any) => item )
                                  )
                              }
                              if (key === 'hour') {
                                setCurrentScheduleId(e.value)
                              }
                            }}
                            disabled= {disabledFieldsObject[key]}
                          />
                          )))
                        : (
                          <div className="skeleton-global-points">
                            <FormSkeleton length={3} />
                          </div>
                        )
                    }
                  </div>
                  <div className="schedules-load-button">
                    <div className="button-container">
                      <Button
                        variant="p-button"
                        label={t('PointsOfAttention.loadButton')}
                        disabled={
                          !(dropdownStates.typeOfSchedule !== '' &&
                          dropdownStates.day !== '' &&
                          dropdownStates.hour !== '')
                        }
                        onClick={() => {
                          setIsOkToLoaded(true)
                          const scheduleToPush = dataSchedule.find((item:any)=> currentScheduleId === item.id)
                          const schedulesPerDay = schedulesSelected?.filter((item:any)=>item.day === scheduleToPush.day)
                          ?.sort(function (a:any, b:any) {
                            if (a.scheduleTimes[0].start > b.scheduleTimes[0].start) {
                              return 1;
                            }
                            if (a.scheduleTimes[0].start < b.scheduleTimes[0].start) {
                              return -1;
                            }
                            return 0;
                          });
                          let isScheduleOk:boolean = true
                          schedulesPerDay.map((item:any)=>{
                            if (!(scheduleToPush.scheduleTimes[0].end <=  item.scheduleTimes[0].start
                                || scheduleToPush.scheduleTimes[0].start >= item.scheduleTimes[0].end))
                            {
                              isScheduleOk = false
                            }
                          })
                          if (isScheduleOk) {
                            let schedulesIds = getValues('scheduleIds')
                            schedulesIds.push(currentScheduleId)
                            setValue('scheduleIds', schedulesIds)
                            setSchedulesSelected([])
                            setSchedulesSource([])
                            let schedulesSelectedAux:any = []
                            dataSchedule.map((item:any) => {
                              schedulesIds.includes(item.id)
                              ? schedulesSelectedAux.push(item)
                              : setSchedulesSource((prev:any)=>([
                                ...prev, item
                              ]))
                            })
                            setSchedulesSelected(schedulesSelectedAux)
                            const selectedDay = dataSchedule.filter((item:any)=>item.id === currentScheduleId)[0]?.day!
                            setValueDays(selectedDay)
                            setSchedulesToShow(schedulesSelectedAux.filter((item:any) => selectedDay === item.day))
                            setDropdownStates({
                              typeOfSchedule: '',
                              day: '',
                              hour: '',
                            })
                            setDisabledFieldsObject((prev:any) => ({
                              ...prev,
                              day: true,
                              hour: true,
                            }))
                          } else {
                            setValueDays(null)
                            setIsOkToLoaded(false)
                          }
                        }}
                      />
                    </div>
                  </div>
              </div>
            </div>
            <div className={`treeboxes-child general-form mid schedules-points--container-box-two`}>
              <div className="treeboxes-item tabletsecond treeboxes-item__white" style={{ height: '80%' }} id='SelectScheduleContainer'>
                  <p className="points-subtitle">{t("PointsOfAttention.schedulesTwoBoxDescription")}</p>
                  <div className="schedules-days-container">
                    <SelectButton
                      value={valueDays}
                      onChange={(e) => {
                        setIsOkToLoaded(true)
                        setValueDays(e.value)
                        setSchedulesToShow(schedulesSelected.filter((item:any) => e.value === item.day))
                      }}
                      optionLabel="name"
                      options={items}
                    />
                  </div>
                  <div className="schedules-scrollbar" style={{ height: '80%' }}>
                    <PerfectScrollbar>
                      {
                        isOkToLoaded ?
                        schedulesToShow.map((schedule:any)=>(
                          <div className="flex mb-1">
                            <Card
                              key={schedule.id}
                            >
                                <p>
                                  {localStorage.getItem('LANGUAGE') === 'es' && 'Horario '}
                                  {t(`PointsOfAttention.typeOfSchedule${
                                      schedule.scheduleType.name === 'NORMAL'
                                      ? 'REGULAR'
                                      : schedule.scheduleType.name}`)}
                                  {localStorage.getItem('LANGUAGE') === 'en' && ' schedule'}
                                </p>
                                <p>{`${formatHour12(schedule.scheduleTimes[0].start)} - ${formatHour12(schedule.scheduleTimes[0].end)}`}</p>
                            </Card>
                            <Button
                              icon="iu iu-icon-delete"
                              style={{
                                color: 'red',
                                alignSelf: 'center',
                              }}
                              id='deleted-button-selectcard'
                              variant='p-button-rounded'
                              onClick={() => {
                                let itemToSourceAux:any;
                                setSchedulesSelected(schedulesSelected.filter((item:any) => {
                                  if (item.id === schedule.id) itemToSourceAux = item;
                                  return item.id !== schedule.id
                                }))
                                setValue('scheduleIds', getValues('scheduleIds').filter((id:any) => id !== schedule.id))
                                setSchedulesToShow(schedulesToShow.filter((item:any) => item.id !== schedule.id))
                                itemToSourceAux && setSchedulesSource((prev:any)=>([
                                  ...prev,
                                  itemToSourceAux!
                                ]))
                                setDropdownStates(dropdownInitStates);
                              }}
                            />
                          </div>
                        ))
                        :
                        <div className='flex flex-column justify-content-center align-items-center' style={{ height: '100%' }}>
                          <h3
                            style={{
                              color: 'var(--errorColor)',
                              width: 'calc(var(--breakpoint-xss) - var(--space-xxl))',
                              textAlign: 'center',
                            }}
                          >
                            {t('PointsOfAttention.scheduleNotAllowedLabel')}
                          </h3>
                        </div>
                      }
                    </PerfectScrollbar>
                  </div>
              </div>
            </div>
        </div>
          </div>
        </div>
      </div>

      {/* Caja de carga de Imágenes */}
      <div className="schedules-points--fileupload mt-4">
        <div className="schedules-points--fileupload-container px-5">
          <div className="schedules-points--container__title">
              <p className="boxTitle">
                {t('PointsOfAttention.iconPointLabel')}
              </p>
          </div>
          <Controller
              key='file'
              name='file'
              control={control}
              rules={(PointsOfAttentionFormSchema(t) as any).serviceIds}
              render={() => {
                  return (
                    <div className="card mt-4">
                      <FileUpload
                        accept="image/*"
                        // maxFileSize={1000000}
                        emptyTemplate={
                          (update && selectedPointsObject?.layerName !== '' && selectedPointsObject?.layerSize !== '')
                            ? <div
                                className="flex align-items-center pl-3"
                                style={{
                                  height: '100%',
                                  justifyContent: 'space-around',
                                }}
                              >
                                <div
                                  style={{
                                    height: '80%',
                                    backgroundColor: 'var(--cardBackgroundColorTwoShade20)',
                                    width: 'var(--space-xxxl)',
                                    borderRadius: '0.25rem',
                                  }}
                                />
                                <div>
                                  {selectedPointsObject?.layerName}
                                </div>
                                <div>
                                  {selectedPointsObject?.layerSize}
                                </div>
                              </div>
                            : <></>
                        }
                        cancelOptions={{
                          style: {
                            display: 'none',
                          }
                        }}
                        uploadOptions={{
                          style: {
                            display: 'none',
                          }
                        }}
                        chooseLabel={t("PointsOfAttention.fileLabel")}
                        chooseOptions={{
                          icon: 'iu iu-icon-upload',
                          className: disableUploadFile ? ' p-disabled' : 'p-button-link',
                        }}
                        onSelect={(e)=>{
                          setValue('file', e.files[0]);
                          setDisableUploadFile(true);
                        }}
                        onRemove={()=>{
                          setValue('file', '');
                          setDisableUploadFile(false);
                        }}
                      />
                    </div>
                  );
                }}
            />
        </div>    
      </div>

      {/* Buttons */}
      <div className="create-points-buttons">
        <div
          className="ap-selector-btn-c my-5 gap-5 create-points-buttons__button-container px-5"
        >
          <Button
            variant="p-button-link"
            label={t("Products.cancelBtnLabel")}
            onClick={
              update
              ? 
                (
                  !isDirty &&
                  (
                    JSON.stringify(serviceIdsValues?.sort((a:number, b:number) => a - b)) ===
                    JSON.stringify(selectedPointsObject?.serviceIds?.sort((a:number, b:number) => a - b))
                  ) &&
                  (
                    JSON.stringify(scheduleIdsValues?.sort((a:number, b:number) => a - b)) ===
                    JSON.stringify(selectedPointsObject?.scheduleIds?.sort((a:number, b:number) => a - b))
                  )
                )
                ? onReturn
                : () => handleModalProps("cancel")
              :
                (!isDirty &&
                serviceIdsValues.length === 0 &&
                scheduleIdsValues.length === 0) ? onReturn : () => handleModalProps("cancel")}
          />
          <Button
            variant="p-button"
            label={t(`Products.${update || updateMultiple ? 'save' : 'create'}BtnLabel`)}
            disabled={
              updateMultiple 
                ?
                  !(
                    observationValues !== '' &&
                    serviceIdsValues.length > 0 &&
                    scheduleIdsValues.length > 0 &&
                    isValid
                  )
                :
                  update
                    ?
                      !(
                        (
                          isDirty ||
                          (
                            JSON.stringify(serviceIdsValues?.sort((a:number, b:number) => a - b)) !==
                            JSON.stringify(selectedPointsObject?.serviceIds?.sort((a:number, b:number) => a - b))
                          ) ||
                          (
                            JSON.stringify(scheduleIdsValues?.sort((a:number, b:number) => a - b)) !==
                            JSON.stringify(selectedPointsObject?.scheduleIds?.sort((a:number, b:number) => a - b))
                          ) ||
                          (fileValues !== '')
                        ) &&
                        isValid &&
                        serviceIdsValues.length > 0 &&
                        scheduleIdsValues.length > 0 &&
                        countryValues !== '' &&
                        departmentValues !== '' &&
                        cityValues !== ''
                      )
                    :
                      !(
                        pointServiceTypeValues !== '' &&
                        phoneValues !== '' &&
                        nameValues !== '' &&
                        codeValues !== '' &&
                        observationValues !== '' &&
                        countryValues !== '' &&
                        departmentValues !== '' &&
                        cityValues !== '' &&
                        addressValues !== '' &&
                        latitudeValues !== '' &&
                        longitudeValues !== '' &&
                        serviceIdsValues.length > 0 &&
                        scheduleIdsValues.length > 0 &&
                        isValid
                      )
            }
            onClick={() => handleModalProps("action")}
          />
        </div>
      </div>

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
    )
  }
  